/* eslint-disable */
import localforage from 'localforage'
// src is a alias. see client/build/webpack.base.conf.js
import { userTokenStorageKey, userDataStorageKey } from '../../../config'
import { isEmpty } from 'lodash'
import * as TYPES from './mutations-types'
import * as services from '../services'

// Read cached user-data directly from localStorage. The durable cache survives
// logout (Vuex state.user is wiped on logout to keep getters like currentUser /
// mainMenu / isShopFloorWorker clean), so when the next login response arrives
// with null'd translations/statuses/appSkin (because we sent matching cache
// headers), we must fall back to localStorage — not state.user — for the merge.
function getCachedUser() {
  try {
    const raw = localStorage.getItem(userDataStorageKey)
    if (!raw) return {}
    return JSON.parse(raw) || {}
  } catch (e) {
    return {}
  }
}

export const attemptLogin = ({ dispatch }, payload) =>
  services.postLogin(payload).then(({ token, user }) => {
    dispatch('setUser', user.data)
    dispatch('setToken', token)
    // Fire-and-forget: load tours so Welcome modal / Help menu have data.
    dispatch('Tours/fetchTours', null, { root: true }).catch(() => {})

    return user // keep promise chain
  })

export const attemptSignup = ({ dispatch }, payload) =>
  services.postSignup(payload).then((response) => {
    const { token, user } = response
    dispatch('setUser', user.data)
    dispatch('setToken', token)

    return response // include emailVerificationSent flag for the caller
  })

export const logout = ({ dispatch }) => {
  services.revokeToken()
  // call actions
  return Promise.all([
    dispatch('setToken', null),
    dispatch('setUser', {}),
    dispatch('wizardSetData', { status: null }),
  ])
}

export const setUser = ({ commit }, user) => {
  // Merge the 3 cacheable metadata blobs from the previous user-data with the
  // incoming response. The backend nulls out translations / appSkin (and
  // always nulls statuses on a cache hit) when the client's cached versions
  // are still valid — we have to fill them back in here so consumers see a
  // complete metadata object. Skip on logout / empty payloads.
  if (user && user.metadata) {
    const prev = getCachedUser()
    const prevMeta = prev.metadata || {}
    const newMeta = user.metadata
    const merged = {
      ...newMeta,
      translations: (newMeta.translations && Object.keys(newMeta.translations).length > 0)
        ? newMeta.translations
        : prevMeta.translations,
      statuses: (newMeta.statuses && Object.keys(newMeta.statuses).length > 0)
        ? newMeta.statuses
        : prevMeta.statuses,
      appSkin: newMeta.appSkin
        ? newMeta.appSkin
        : prevMeta.appSkin,
      resources: newMeta.resources  // resources should be always replaced
        ? newMeta.resources
        : prevMeta.resources,
    }
    user = { ...user, metadata: merged }
  }
  commit(TYPES.SET_USER, user)

  return Promise.resolve(user) // keep promise chain
}

export const setToken = ({ commit }, payload) => {
  // prevent if payload is a object
  const token = isEmpty(payload) ? null : payload.token || payload
  
  // Commit the mutations
  commit(TYPES.SET_TOKEN, token)

  return Promise.resolve(token) // keep promise chain
}

export const checkUserToken = ({ dispatch, state }) => {
  // If the token exists then all validation has already been done
  if (!isEmpty(state.token)) {
    return Promise.resolve(state.token)
  }

  /**
   * Token does not exist yet
   * - Recover it from localstorage
   * - Recover also the user, validating the token also
   */
  return (
    localforage
      .getItem(userTokenStorageKey)
      .then((token) => {
        if (isEmpty(token)) {
          // Token is not saved in localstorage
          return Promise.reject('NO_TOKEN') // Reject promise
        }
        // Put the token in the vuex store
        return dispatch('setToken', token) // keep promise chain
      })
      // With the token in hand, retrieves the user's data, validating the token
      .then(() => dispatch('loadUser'))
  )
}

/**
 * Retrieves updated user information
 * If something goes wrong, the user's token is probably invalid
 */
export const loadUser = ({ dispatch }) => {
  services
    .loadUserData()
    // store user's data
    .then((user) => {
      return dispatch('setUser', user.data).then(() => {
        // Fire-and-forget: load tours so Welcome modal / Help menu have data.
        // Never block login on a tours fetch failure.
        dispatch('Tours/fetchTours', null, { root: true }).catch(() => {})
        return user
      })
    })
    .catch(() => {
      // Process failure, delete the token
      dispatch('setToken', '')
      return Promise.reject('FAIL_IN_LOAD_USER') // keep promise chain
    })}
